/**
 * VX-2 X1 — 「지금(위성) ↔ 완공 상상도」 비교 슬라이더의 «판정·주소 변환» (순수 · 의존 0 · 클라이언트에서도 import 가능).
 *
 * 대상: 그 현장의 S1(없으면 S2) 컷이 «위성 참고 방식»(image_jobs.gen_method = 'vx1_sat_ref')으로 생성된 경우만.
 *   위성과 상상도의 위치가 겹쳐야 비교가 성립한다 — 텍스트 방식(vx1_text·vx1_text_spec)·v1(null)은 내지 않는다.
 *
 * 「생성에 쓴 바로 그 위성」 — 작업자가 image_jobs.slots 에 남긴 값을 읽는다(2026-10-12 실측):
 *   · sat_ref_kind 'centered' + sat_ref_path 'satref/{site_id}.webp'  (gen-images 버킷 · 좌표 정중앙 스티칭 1024²)
 *   · sat_ref_kind 'legacy'   + sat_ref_url  '…/storage/v1/object/public/images/satellite/{site_id}.webp' (기존 수집분 1024²)
 *   현재 표본 4곳은 전부 legacy 다(site_sat_refs 0행).
 *
 * ⛔ Storage 직링크(supabase.co)는 «한 글자도» 화면·RSC 페이로드로 내보내지 않는다(G-VX2). 이 모듈은 자체 도메인
 *    상대경로만 돌려준다: `/img-sat/{site_id}.webp` · `/img-sat/legacy/{site_id}.webp` (next.config rewrite · X-Robots-Tag noindex).
 * ⛔ 주소·키에 든 site_id 가 그 현장의 id 와 다르면 버린다(오귀속 방지 — 다른 현장의 위성을 「지금」 이라 내지 않는다).
 */
import type { ResolvedSiteImage, SiteImageRow, GeneratedShot } from './pure';
import { withVworldAttribution } from '@/lib/attribution/vworld';

export const SAT_REF_METHOD = 'vx1_sat_ref';
export const IMG_SAT_PREFIX = '/img-sat';
/** 두 경로 모두 1024×1024 정사각(satref.ts 가 768 창을 1024 로 올려 저장 · 기존 수집분도 1024²). */
export const SAT_REF_SIZE = 1024;
/** 슬라이더가 고르는 컷 — 항공 컷만(위성과 시점이 통하는 것). S1 우선. */
export const COMPARE_SHOTS = ['S1_aerial_day', 'S2_aerial_golden'] as const;

/** post1 1b — 화면은 BASE + 링크형 출처(VworldCredit). 평문이 필요한 자리는 COMPARE_CAPTION(출처 포함). */
export const COMPARE_CAPTION_BASE = '현재 위성사진 · 완공 후 AI 상상도 — 실제와 다를 수 있음';
export const COMPARE_CAPTION = withVworldAttribution(COMPARE_CAPTION_BASE);

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const SATREF_KEY_RE = new RegExp(`^satref/(${UUID})\\.webp$`);
const LEGACY_URL_RE = new RegExp(`^https://(?:[a-z0-9]{20}|kadeora)\\.supabase\\.co/storage/v1/object/public/images/satellite/(${UUID})\\.webp(?:\\?[^\\s#]*)?$`, 'i');

export type SiteImageRowWithJob = SiteImageRow & { job_id?: string | null };

/** image_jobs 부분 행(조회 열: id, ref_site_id, gen_method, slots). */
export interface CompareJobRow {
  id?: string | null;
  ref_site_id?: string | null;
  gen_method?: string | null;
  slots?: Record<string, unknown> | null;
}

/** 서버가 미리 읽어 두는 값 — «자체 도메인 상대경로» 만 담는다(직링크 없음). */
export interface CompareSatRef {
  shot: string;
  kind: 'centered' | 'legacy';
  /** `/img-sat/…` 상대경로. */
  path: string;
  width: number;
  height: number;
}

/** 슬라이더 대상 컷의 행 — S1(없으면 S2) · visible · job_id 보유. 없으면 null. */
export function compareRowOf(rows: readonly SiteImageRowWithJob[] | null | undefined): SiteImageRowWithJob | null {
  for (const code of COMPARE_SHOTS) {
    const row = (rows ?? []).find((r) => r?.shot === code && r.visible !== false);
    // S1 이 있으면 S1 로 «확정» 한다 — S1 이 텍스트 방식인데 S2 로 넘어가 슬라이더를 내지 않는다(히어로 그림과 다른 컷을 비교하게 된다).
    if (row) return typeof row.job_id === 'string' && row.job_id.trim() ? row : null;
  }
  return null;
}

/** Storage 키 `satref/{site_id}.webp` → `/img-sat/{site_id}.webp`. 그 밖·다른 현장 → null. */
export function satRefPathFromKey(key: unknown, siteId: string): string | null {
  if (typeof key !== 'string') return null;
  const m = SATREF_KEY_RE.exec(key.trim());
  if (!m || m[1].toLowerCase() !== siteId.trim().toLowerCase()) return null;
  return `${IMG_SAT_PREFIX}/${m[1].toLowerCase()}.webp`;
}

/** 기존 위성 직링크(images/satellite/{site_id}.webp) → `/img-sat/legacy/{site_id}.webp`. 그 밖·다른 현장 → null. */
export function satRefPathFromLegacyUrl(url: unknown, siteId: string): string | null {
  if (typeof url !== 'string') return null;
  const m = LEGACY_URL_RE.exec(url.trim());
  if (!m || m[1].toLowerCase() !== siteId.trim().toLowerCase()) return null;
  return `${IMG_SAT_PREFIX}/legacy/${m[1].toLowerCase()}.webp`;
}

/**
 * 작업 행 → 비교용 위성 참조. 위성 참고 방식이 아니거나, 그 작업이 쓴 위성을 자체 경로로 바꿀 수 없으면 null.
 *   row = compareRowOf 가 고른 site_images 행(그 행의 job_id 와 작업 id 가 같아야 한다).
 */
export function compareSatRefOf(row: SiteImageRowWithJob | null | undefined, job: CompareJobRow | null | undefined): CompareSatRef | null {
  if (!row || !job) return null;
  const siteId = String(row.site_id ?? '').trim().toLowerCase();
  if (!siteId) return null;
  if (String(job.id ?? '').trim().toLowerCase() !== String(row.job_id ?? '').trim().toLowerCase()) return null;
  if (job.ref_site_id != null && String(job.ref_site_id).trim().toLowerCase() !== siteId) return null;
  if (job.gen_method !== SAT_REF_METHOD) return null;
  const slots = job.slots && typeof job.slots === 'object' ? job.slots : null;
  if (!slots) return null;
  const centered = satRefPathFromKey(slots.sat_ref_path, siteId);
  if (centered) return { shot: row.shot, kind: 'centered', path: centered, width: SAT_REF_SIZE, height: SAT_REF_SIZE };
  const legacy = satRefPathFromLegacyUrl(slots.sat_ref_url, siteId);
  if (legacy) return { shot: row.shot, kind: 'legacy', path: legacy, width: SAT_REF_SIZE, height: SAT_REF_SIZE };
  return null;
}

/** 화면 컴포넌트(CompareSlider)의 입력. */
export interface CompareSliderData {
  shot: string;
  before: { src: string; alt: string; width: number; height: number };
  after: { src: string; webp?: string; alt: string; caption: string; width: number; height: number };
}

/** 위성 이미지 alt — 상상도 alt 와 반드시 다르다. */
export function satelliteAltOf(displayName: string | null | undefined): string {
  const n = (displayName ?? '').replace(/\s+/g, ' ').trim();
  return n ? `${n} 현재 위성사진` : '현재 위성사진';
}

/**
 * 리졸버 결과 + 미리 읽은 위성 참조 → 슬라이더 입력. 하나라도 어긋나면 null(= 슬라이더 미노출).
 *   · 생성(generated) 판정이 아니면 null — 게이트 닫힘·공식 사진 현장·카드는 여기서 끝난다.
 *   · 위성 참조의 컷이 리졸버가 통과시킨 컷에 없으면 null(행은 있으나 파생본이 빠진 경우).
 *   · 상상도 = master JPEG 절대 URL(색인 대상) + 표시용 gallery webp(있으면 <source>).
 */
export function compareSliderOf(
  resolved: ResolvedSiteImage | null | undefined,
  sat: CompareSatRef | null | undefined,
  displayName: string | null | undefined,
): CompareSliderData | null {
  if (!resolved || resolved.kind !== 'generated' || !resolved.gateOpen || !sat) return null;
  if (!(COMPARE_SHOTS as readonly string[]).includes(sat.shot)) return null;
  if (!sat.path.startsWith(`${IMG_SAT_PREFIX}/`)) return null;
  const shot: GeneratedShot | undefined = resolved.shots.find((s) => s.shot === sat.shot);
  if (!shot) return null;
  const beforeAlt = satelliteAltOf(displayName);
  if (beforeAlt === shot.alt) return null;
  return {
    shot: shot.shot,
    before: { src: sat.path, alt: beforeAlt, width: sat.width, height: sat.height },
    after: {
      src: shot.master.url,
      ...(shot.gallery ? { webp: shot.gallery.path } : {}),
      alt: shot.alt,
      caption: shot.caption,
      width: shot.master.width,
      height: shot.master.height,
    },
  };
}

/** 슬라이더 위치 0~100 으로 자른다(NaN → 50). */
export function clampComparePos(v: number): number {
  if (!Number.isFinite(v)) return 50;
  return Math.max(0, Math.min(100, Math.round(v * 10) / 10));
}

/** 키 조작 → 다음 위치. 해당 없는 키면 null. ← 는 「지금」(왼쪽) 을 줄이고 → 는 늘린다. */
export function compareKeyStep(key: string, pos: number): number | null {
  switch (key) {
    case 'ArrowLeft': case 'ArrowDown': return clampComparePos(pos - 5);
    case 'ArrowRight': case 'ArrowUp': return clampComparePos(pos + 5);
    case 'PageDown': return clampComparePos(pos - 20);
    case 'PageUp': return clampComparePos(pos + 20);
    case 'Home': return 0;
    case 'End': return 100;
    default: return null;
  }
}
